import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { joiResolver } from '@hookform/resolvers/joi';
import { useSnackbar } from 'notistack';
import { useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';

import { usePartnersOfferAction, usePartnersSaveOffer } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import { type PartnerOffer } from '@/api/routes/partners/partners.offers.api';
import Button from '@/components/Button';
import FormGlobalError from '@/components/forms/FormGlobalError';
import FormSectionHeader from '@/components/forms/FormSectionHeader';
import OfferActionsFields from '@/domain/offers/components/OfferActionsFields';
import OfferBenefitFields from '@/domain/offers/components/OfferBenefitFields';
import OfferFormField from '@/domain/offers/components/OfferFormField';
import OfferImageField from '@/domain/offers/components/OfferImageField';
import OfferLocationsFields from '@/domain/offers/components/OfferLocationsFields';
import OfferPreviewCard from '@/domain/offers/components/OfferPreviewCard';
import { buildOfferSchema } from '@/domain/offers/schemas';
import { type OfferFormIntent, type OfferFormValues } from '@/domain/offers/types';
import {
  OFFER_LIMITS,
  canSubmitOfferFromForm,
  emptyOfferFormValues,
  fromPartnerOffer,
  getOfferFormSaveMode,
  isOfferFormVisibleField,
  needsOfferLocation,
  toPartnerOfferBody,
} from '@/domain/offers/utils';
import { useApiFormErrors, useServerErrorsStore } from '@/hooks/useApiFormErrors';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { withServerErrors } from '@/utils/errors';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  partner: Partner;
  /** The offer being edited; a new offer has none. */
  offer?: PartnerOffer;
}

/**
 * P18 form, for a new offer and for editing a draft, a rejected one or one waiting for changes.
 * An approved account can save and send in one go; the preview shows the app card.
 */
const OfferEditor = ({ partner, offer }: Props) => {
  const t = useTranslate('domain.offers.components.OfferEditor');
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const mode = getOfferFormSaveMode(offer);
  const canSubmit = canSubmitOfferFromForm(partner.status);
  const intentRef = useRef<OfferFormIntent>('save');

  const serverErrors = useServerErrorsStore();
  const resolver = useMemo(
    () => withServerErrors(joiResolver(buildOfferSchema()), serverErrors),
    [serverErrors],
  );
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { isDirty },
  } = useForm<OfferFormValues>({
    resolver,
    mode: 'onTouched',
    defaultValues: offer ? fromPartnerOffer(offer) : emptyOfferFormValues(),
  });
  const { applyApiError, globalError, clearGlobalError, clearApiErrors } =
    useApiFormErrors<OfferFormValues>({
      setError,
      getValues,
      serverErrors,
      isVisibleField: isOfferFormVisibleField,
      fallbackMessage: t('saveError'),
    });
  // Every field has a default, so the watched values are complete.
  const preview = useWatch({ control }) as OfferFormValues;

  const { mutateAsync: save, isPending: isSaving } = usePartnersSaveOffer(offer?.id);
  const { mutateAsync: submit, isPending: isSubmitting } = usePartnersOfferAction('submit');
  const isBusy = isSaving || isSubmitting;

  const persist = async (values: OfferFormValues) => {
    const intent = intentRef.current;
    clearApiErrors();
    let savedId: string;
    try {
      savedId = (await save(toPartnerOfferBody(values))).offer.id;
    } catch (error) {
      applyApiError(error);
      return;
    }

    if (intent === 'submit') {
      try {
        await submit(savedId);
        enqueueSnackbar(t('submitted'), { variant: 'success' });
      } catch {
        // Saved; the API reason is in the global snackbar.
      }
    } else {
      enqueueSnackbar(t('saved'), { variant: 'success' });
    }
    navigate(generatePath(ScreenRoutes.OFFER_DETAIL, { offerId: savedId }), { replace: true });
  };

  const submitWith = (intent: OfferFormIntent) => () => {
    intentRef.current = intent;
  };

  const cancelEdit = () =>
    navigate(
      offer ? generatePath(ScreenRoutes.OFFER_DETAIL, { offerId: offer.id }) : ScreenRoutes.OFFERS,
    );

  return (
    <Box component="form" noValidate onSubmit={handleSubmit(persist)} sx={sx.root}>
      <Stack gap={0.75}>
        <Typography component="h1" variant="h2">
          {offer ? t('editTitle') : t('createTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subtitle')}
        </Typography>
      </Stack>

      {offer && offer.reviewNote && offer.status !== 'draft' ? (
        <Alert severity={offer.status === 'rejected' ? 'error' : 'warning'}>
          <AlertTitle>{t(`reviewNoteTitle.${mode}`)}</AlertTitle>
          {offer.reviewNote}
        </Alert>
      ) : null}
      <FormGlobalError message={globalError} onClose={clearGlobalError} />

      <Box sx={sx.columns}>
        <Paper variant="outlined" sx={sx.paper}>
          <Stack divider={<Box sx={sx.divider} />}>
            <Box sx={sx.section}>
              <FormSectionHeader n={1} title={t('sections.image')} hint={t('optional')} />
              <OfferImageField control={control} />
            </Box>
            <Box sx={sx.section}>
              <FormSectionHeader n={2} title={t('sections.text')} hint={t('textHint')} />
              <Stack gap={2.5}>
                <OfferFormField
                  control={control}
                  name="title"
                  label={t('title')}
                  maxChars={OFFER_LIMITS.titleMax}
                />
                <OfferFormField
                  control={control}
                  name="body"
                  label={t('body')}
                  maxChars={OFFER_LIMITS.bodyMax}
                  multiline
                  minRows={3}
                />
              </Stack>
            </Box>
            <Box sx={sx.section}>
              <FormSectionHeader n={3} title={t('sections.benefit')} hint={t('optional')} />
              <OfferBenefitFields control={control} />
            </Box>
            <Box sx={sx.section}>
              <FormSectionHeader n={4} title={t('sections.actions')} hint={t('actionsHint')} />
              <OfferActionsFields
                control={control}
                setValue={setValue}
                getValues={getValues}
                trigger={trigger}
                clearErrors={clearErrors}
              />
            </Box>
            <Box sx={sx.section}>
              <FormSectionHeader
                n={5}
                title={t('sections.locations')}
                hint={needsOfferLocation(preview.actions) ? t('locationsRequired') : t('optional')}
              />
              <OfferLocationsFields control={control} setValue={setValue} />
            </Box>
            <Box sx={sx.section}>
              <FormSectionHeader n={6} title={t('sections.audience')} hint={t('audienceHint')} />
              <Stack gap={2.5}>
                <OfferFormField
                  control={control}
                  name="desiredAudienceText"
                  label={t('desiredAudienceText')}
                  optionalLabel={t('optional')}
                  placeholder={t('desiredAudiencePlaceholder')}
                  maxChars={OFFER_LIMITS.audienceMax}
                  multiline
                  minRows={2}
                />
                <Box sx={sx.dates}>
                  <OfferFormField
                    control={control}
                    name="desiredStartsAt"
                    label={t('desiredStartsAt')}
                    optionalLabel={t('optional')}
                    type="date"
                  />
                  <OfferFormField
                    control={control}
                    name="desiredEndsAt"
                    label={t('desiredEndsAt')}
                    optionalLabel={t('optional')}
                    type="date"
                  />
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>
        <Box sx={sx.preview}>
          <OfferPreviewCard
            partnerName={partner.displayName}
            title={preview.title}
            body={preview.body}
            imageUrl={preview.imageUrl || null}
            couponCode={(preview.benefit.enabled && preview.benefit.code.trim()) || null}
            actions={preview.actions}
            locations={preview.locations}
          />
        </Box>
      </Box>

      <Box sx={sx.footer}>
        <Button variant="text" onClick={cancelEdit} disabled={isBusy}>
          {offer ? t('discard') : t('cancel')}
        </Button>
        <Stack direction={{ xs: 'column-reverse', sm: 'row' }} gap={1.5} alignItems="stretch">
          <Button
            type="submit"
            variant="outlined"
            onClick={submitWith('save')}
            loading={isSaving && intentRef.current === 'save'}
            disabled={isBusy || (Boolean(offer) && !isDirty)}
          >
            {mode === 'draft' ? t('saveDraft') : t('saveChanges')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={submitWith('submit')}
            loading={isBusy && intentRef.current === 'submit'}
            disabled={!canSubmit || isBusy}
          >
            {mode === 'draft' ? t('saveAndSubmit') : t('saveAndResubmit')}
          </Button>
        </Stack>
      </Box>
      {!canSubmit ? (
        <Typography variant="caption" color="text.secondary" textAlign="right">
          {t('submitLocked')}
        </Typography>
      ) : null}
    </Box>
  );
};

export default OfferEditor;

const sx = {
  root: {
    width: '100%',
    maxWidth: 1120,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  columns: {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 340px' },
  },
  preview: { position: { md: 'sticky' }, top: { md: 24 } },
  paper: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
  section: { p: { xs: 2.5, sm: 3.5 } },
  divider: { borderTop: 1, borderColor: 'divider' },
  dates: { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'stretch', sm: 'center' },
    flexDirection: { xs: 'column-reverse', sm: 'row' },
    gap: 2,
  },
};
